import { getFilmFormat } from '@/content/instantFilm'
import { toast } from 'sonner'
import {
  NodeKind,
  TransformKind,
  collageCellContext,
  createNodeId,
  documentOutputRatio,
  fitCollageCells,
  replaceNode,
  type FrameNode,
  type ImageNode,
} from '@/core/document'
import {
  centeredPresetRectInside,
  largestRectForRatioInside,
  orientationCropBounds,
  type AspectRatio,
} from '@/core/geometry'
import { useEditSession } from '@/core/store/editSession'
import { EditAction } from '@/core/history'
import { selectParentFrame, selectSelectedNode } from '@/core/store/selectors'
import {
  FREE_PLACEMENT_MAX_OFFSET,
  FREE_PLACEMENT_MAX_SCALE,
  FREE_PLACEMENT_MIN_SCALE,
  PhotoPlacement,
  FrameStyleId,
  createDefaultPolaroidFrameParams,
  type FreePlacement,
  type PolaroidFrameParams,
} from '@/tools/frame/params'

type PolaroidFrameNode = Omit<FrameNode, 'params'> & { params: PolaroidFrameParams }

function cropImageToRatio(node: ImageNode, ratio: AspectRatio): ImageNode {
  const state = useEditSession.getState()
  const source = state.sources.get(node.sourceId)
  if (!source) throw new Error(`Cannot crop framed photo: source "${node.sourceId}" is missing`)
  const bounds = orientationCropBounds(source, node.transforms[TransformKind.Orientation])
  const currentCrop = node.transforms[TransformKind.Crop]
  const rect = currentCrop
    ? centeredPresetRectInside(currentCrop.rect, bounds, ratio)
    : largestRectForRatioInside(bounds, ratio)
  return { ...node, transforms: { ...node.transforms, [TransformKind.Crop]: { rect } } }
}

function selectedFrame(): { state: ReturnType<typeof useEditSession.getState>; frame: PolaroidFrameNode } {
  const state = useEditSession.getState()
  const frame = selectParentFrame(state)
  if (!frame) throw new Error('Polaroid settings require the active photo to have a parent frame')
  if (frame.params.styleId !== FrameStyleId.Polaroid) {
    throw new Error(`Polaroid settings require a Polaroid frame, received "${frame.params.styleId}"`)
  }
  return { state, frame: { ...frame, params: frame.params } }
}

function updateSelectedFrame(
  updater: (frame: PolaroidFrameNode) => PolaroidFrameNode,
  commit: boolean,
): void {
  const { state, frame } = selectedFrame()
  state.updateNode(frame.id, (node) => {
    if (node.kind !== NodeKind.Frame) throw new Error('Polaroid update target changed node kind')
    if (node.params.styleId !== FrameStyleId.Polaroid) throw new Error('Polaroid update target changed frame style')
    return updater({ ...node, params: node.params })
  })
  if (commit) useEditSession.getState().commitHistory(EditAction.EditFrame)
}

export function updateSelectedFrameParams(
  partial: Partial<PolaroidFrameParams>,
  commit = true,
): void {
  updateSelectedFrame((frame) => ({
    ...frame,
    params: { ...frame.params, ...partial },
  }), commit)
}

export function setSelectedFramePaperColor(paperColor: string, commit = true): void {
  updateSelectedFrameParams({ paperColor }, commit)
}

export function setSelectedFrameFormat(formatId: PolaroidFrameParams['formatId']): void {
  const format = getFilmFormat(formatId)
  updateSelectedFrame((frame) => {
    const child = frame.params.placement === PhotoPlacement.Fill
      ? cropFrameChildToRatio(frame.child, { w: format.windowMm.width, h: format.windowMm.height })
      : frame.child
    return { ...frame, params: { ...frame.params, formatId }, child }
  }, true)
}

function cropFrameChildToRatio(child: FrameNode['child'], ratio: AspectRatio): FrameNode['child'] {
  if (child.kind !== NodeKind.Image) {
    throw new Error(`Fill placement requires an Image child in M5, received "${child.kind}"`)
  }
  return cropImageToRatio(child, ratio)
}

export function setSelectedPhotoPlacement(placement: PhotoPlacement): void {
  if (!Object.values(PhotoPlacement).includes(placement)) {
    throw new Error(`Unknown photo placement: "${String(placement)}"`)
  }
  updateSelectedFrame((frame) => {
    if (placement === PhotoPlacement.Free) {
      return {
        ...frame,
        params: {
          ...frame.params,
          placement,
          free: { scale: 1, offsetX: 0, offsetY: 0 },
        },
      }
    }
    const format = getFilmFormat(frame.params.formatId)
    return {
      ...frame,
      params: { ...frame.params, placement, free: null },
      child: cropFrameChildToRatio(frame.child, {
        w: format.windowMm.width,
        h: format.windowMm.height,
      }),
    }
  }, true)
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value))
}

export function updateSelectedFreePlacement(free: FreePlacement, commit = false): void {
  if (![free.scale, free.offsetX, free.offsetY].every(Number.isFinite)) {
    throw new Error('Free photo placement requires finite scale and offsets')
  }
  updateSelectedFrame((frame) => {
    if (frame.params.placement !== PhotoPlacement.Free || !frame.params.free) {
      throw new Error('Free photo placement updates require a frame in Free mode')
    }
    return {
      ...frame,
      params: {
        ...frame.params,
        free: {
          scale: clamp(free.scale, FREE_PLACEMENT_MIN_SCALE, FREE_PLACEMENT_MAX_SCALE),
          offsetX: clamp(free.offsetX, -FREE_PLACEMENT_MAX_OFFSET, FREE_PLACEMENT_MAX_OFFSET),
          offsetY: clamp(free.offsetY, -FREE_PLACEMENT_MAX_OFFSET, FREE_PLACEMENT_MAX_OFFSET),
        },
      },
    }
  }, commit)
}

export function setSelectedFreePlacementScale(scale: number, commit = false): void {
  const { frame } = selectedFrame()
  if (frame.params.placement !== PhotoPlacement.Free || !frame.params.free) {
    throw new Error('Print size updates require a frame in Free mode')
  }
  updateSelectedFreePlacement({ ...frame.params.free, scale }, commit)
}

export function recenterSelectedFreePlacement(): void {
  updateSelectedFreePlacement({ scale: 1, offsetX: 0, offsetY: 0 }, true)
}

export function addPolaroidFrame(): void {
  const state = useEditSession.getState()
  const selected = selectSelectedNode(state)
  const existingFrame = selectParentFrame(state)
  if (existingFrame?.params.styleId === FrameStyleId.Polaroid) {
    throw new Error('The active photo already has a Polaroid frame')
  }
  const source = state.sources.get(selected.sourceId)
  if (!source) throw new Error(`Cannot add Polaroid frame: source "${selected.sourceId}" is missing`)
  const params = createDefaultPolaroidFrameParams(source.capturedDate)
  const format = getFilmFormat(params.formatId)
  const child = cropImageToRatio(selected, { w: format.windowMm.width, h: format.windowMm.height })
  const frame: FrameNode = {
    id: createNodeId(),
    kind: NodeKind.Frame,
    params,
    child,
  }
  if (existingFrame) {
    if (!state.document) throw new Error('Replacing a Plain frame requires a loaded document')
    if (existingFrame.params.styleId !== FrameStyleId.Plain) {
      throw new Error(`Cannot replace unknown frame style "${String((existingFrame.params as { styleId: unknown }).styleId)}"`)
    }
    if (!collageCellContext(state.document.root, existingFrame.id)) {
      throw new Error('A Plain frame can be replaced with Polaroid only inside a collage cell')
    }
    state.replaceDocument(
      { ...state.document, root: replaceNode(state.document.root, existingFrame.id, frame) },
      selected.id,
    )
    useEditSession.getState().commitHistory(EditAction.AddFrame)
    toast.info('Changed Plain frame to Polaroid to support one frame per photo', {
      action: {
        label: 'Undo',
        onClick: () => useEditSession.getState().undo(),
      },
    })
    return
  }
  state.wrapNode(selected.id, frame)
  useEditSession.getState().commitHistory(EditAction.AddFrame)
}

export function removeFrame(): void {
  const state = useEditSession.getState()
  if (!state.document) throw new Error('Removing a frame requires a loaded document')
  const selected = selectParentFrame(state)
  if (!selected) throw new Error('Remove frame requires the active photo to have a parent frame')
  const collageContext = collageCellContext(state.document.root, selected.child.id)
  if (collageContext) {
    const cells = [...collageContext.collage.cells]
    cells[collageContext.cellIndex] = replaceNode(
      collageContext.cellRoot,
      selected.id,
      selected.child,
    )
    const ratio = documentOutputRatio(state.document, state.sources)
    const fitted = fitCollageCells(
      { ...collageContext.collage, cells },
      { width: ratio.w, height: ratio.h },
      state.sources,
    )
    const root = collageContext.collage.id === state.document.root.id
      ? fitted
      : replaceNode(state.document.root, collageContext.collage.id, fitted)
    state.replaceDocument({ ...state.document, root }, selected.child.id)
    useEditSession.getState().commitHistory(EditAction.RemoveFrame)
    return
  }
  const child = selected.id === state.document.root.id && selected.child.kind === NodeKind.Image
    ? cropImageToRatio(selected.child, documentOutputRatio(state.document, state.sources))
    : selected.child
  state.unwrapNode(selected.id, child)
  useEditSession.getState().commitHistory(EditAction.RemoveFrame)
}
